import { c as _c } from "react/compiler-runtime";
import { useTheme } from "@mui/material";
import { OrbitControls } from "@react-three/drei";
import { Canvas } from "@react-three/fiber";
import React from "react";
import { type OrbitControls as ThreeOrbitControls } from "three-stdlib";
import { useMobileLayout } from "#/shared/hooks";
import { OrbitModelCanvasTouchScroll } from "#/shared/ui/molecules/OrbitModelCanvasTouchScroll";
import { PythonLogoModel } from "#/3d-models/PythonLogoModel";
type PythonLogoModelViewProps = {
  controlsRef?: React.RefObject<ThreeOrbitControls | null>;
  interactive?: boolean;
  cameraPosition?: [number, number, number];
  cameraFov?: number;
  target?: [number, number, number];
  distanceRange?: readonly [number, number];
};
export const PythonLogoModelView: React.FC<PythonLogoModelViewProps> = t0 => {
  const $ = _c(37);
  const {
    controlsRef,
    interactive: t1,
    cameraPosition: t2,
    cameraFov: t3,
    target: t4,
    distanceRange: t5
  } = t0;
  const interactive = t1 === undefined ? true : t1;
  let t6;
  if ($[0] !== t2) {
    t6 = t2 === undefined ? [0, 0, 5] : t2;
    $[0] = t2;
    $[1] = t6;
  } else {
    t6 = $[1];
  }
  const cameraPosition = t6;
  const cameraFov = t3 === undefined ? 50 : t3;
  let t7;
  if ($[2] !== t4) {
    t7 = t4 === undefined ? [0, 0.5, 0] : t4;
    $[2] = t4;
    $[3] = t7;
  } else {
    t7 = $[3];
  }
  const target = t7;
  let t8;
  if ($[4] !== t5) {
    t8 = t5 === undefined ? [13, 20] : t5;
    $[4] = t5;
    $[5] = t8;
  } else {
    t8 = $[5];
  }
  const distanceRange = t8;
  const theme = useTheme();
  const isMobile = useMobileLayout();
  const pointerRotationEnabled = interactive && !isMobile;
  const pointerEventsDisabled = isMobile || !interactive;
  let t9;
  if ($[6] !== cameraFov || $[7] !== cameraPosition) {
    t9 = {
      position: cameraPosition,
      fov: cameraFov
    };
    $[6] = cameraFov;
    $[7] = cameraPosition;
    $[8] = t9;
  } else {
    t9 = $[8];
  }
  let t10;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      alpha: true,
      antialias: true
    };
    $[9] = t10;
  } else {
    t10 = $[9];
  }
  let t11;
  if ($[10] !== pointerEventsDisabled) {
    t11 = {
      background: "transparent",
      ...(pointerEventsDisabled ? {
        pointerEvents: "none"
      } : {})
    };
    $[10] = pointerEventsDisabled;
    $[11] = t11;
  } else {
    t11 = $[11];
  }
  let t12;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <ambientLight intensity={2.5} />;
    $[12] = t12;
  } else {
    t12 = $[12];
  }
  let t13;
  let t14;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = [3.592, 5.939, 3.134];
    t14 = [-1.839, 0.602, 1.932];
    $[13] = t13;
    $[14] = t14;
  } else {
    t13 = $[13];
    t14 = $[14];
  }
  let t15;
  if ($[15] !== theme.palette.secondary.main) {
    t15 = <pointLight intensity={2} decay={2} color={theme.palette.secondary.main} position={t13} rotation={t14} />;
    $[15] = theme.palette.secondary.main;
    $[16] = t15;
  } else {
    t15 = $[16];
  }
  let t16;
  let t17;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = [-6.44, -5.881, 2.343];
    t17 = [-1.839, 0.602, 1.932];
    $[17] = t16;
    $[18] = t17;
  } else {
    t16 = $[17];
    t17 = $[18];
  }
  let t18;
  if ($[19] !== theme.palette.primary.light) {
    t18 = <pointLight intensity={2} decay={2} color={theme.palette.primary.light} position={t16} rotation={t17} />;
    $[19] = theme.palette.primary.light;
    $[20] = t18;
  } else {
    t18 = $[20];
  }
  let t19;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <PythonLogoModel />;
    $[21] = t19;
  } else {
    t19 = $[21];
  }
  let t20;
  if ($[22] !== controlsRef || $[23] !== distanceRange[0] || $[24] !== distanceRange[1] || $[25] !== pointerRotationEnabled || $[26] !== target) {
    t20 = <OrbitControls ref={controlsRef} enabled={pointerRotationEnabled} target={target} minAzimuthAngle={Math.PI / -2.2} maxAzimuthAngle={Math.PI / 2.2} minPolarAngle={Math.PI / 10} maxPolarAngle={Math.PI / 1.1} minDistance={distanceRange[0]} maxDistance={distanceRange[1]} enableRotate={pointerRotationEnabled} enableZoom={false} enablePan={false} enableDamping={pointerRotationEnabled} dampingFactor={0.005} />;
    $[22] = controlsRef;
    $[23] = distanceRange[0];
    $[24] = distanceRange[1];
    $[25] = pointerRotationEnabled;
    $[26] = target;
    $[27] = t20;
  } else {
    t20 = $[27];
  }
  let t21;
  if ($[28] !== pointerEventsDisabled) {
    t21 = <OrbitModelCanvasTouchScroll active={pointerEventsDisabled} />;
    $[28] = pointerEventsDisabled;
    $[29] = t21;
  } else {
    t21 = $[29];
  }
  let t22;
  if ($[30] !== t11 || $[31] !== t15 || $[32] !== t18 || $[33] !== t20 || $[34] !== t21 || $[35] !== t9) {
    t22 = <Canvas camera={t9} gl={t10} onCreated={_temp} style={t11}>{t12}{t15}{t18}{t19}{t20}{t21}</Canvas>;
    $[30] = t11;
    $[31] = t15;
    $[32] = t18;
    $[33] = t20;
    $[34] = t21;
    $[35] = t9;
    $[36] = t22;
  } else {
    t22 = $[36];
  }
  return t22;
};
function _temp(t0) {
  const {
    gl
  } = t0;
  gl.setClearColor("#000000", 0);
}